// eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-imports-ts
import { Build, Component, Element, Prop, State, Watch, Host, h } from '@stencil/core';

import { BannerPromotion } from '../../types';
import { createSrcSet, LazyLoadClassName } from '../../utils';
import { createSwitchboardLink, getQueryParamFromUrl } from '../../utils';

import { getPromotionEvent, debouncedLogEvent } from '../../utils/analytics';
import { fireImpressions } from '../../utils/impressions';
import { registerElToObserverList } from '../../utils/observer';
import { getImageSize } from '../bam-banner/utils';
import NoFollowLink from '../functional/NoFollowLink';

@Component({
    tag: 'static-banner',
    styleUrl: 'static-banner.css',
    shadow: true,
})
export class StaticBanner {
    @Element() el: HTMLElement;

    private defaultCallback() {
        return null;
    }

    // props
    @Prop() parentWidth: number;
    @Prop() promotion: BannerPromotion;
    @Prop() propertyId: number;
    @Prop() context: string;
    @Prop() canUseAlternateLink: boolean;
    @Prop() eventOptions: { placementId: number; allPromotions: BannerPromotion[]; league: string; pageSlug: string };
    @Prop() bannerType: 'Banner' | 'Rotating Banner' = 'Banner';
    @Prop() location: string;
    @Prop() callback: () => void = this.defaultCallback;

    // state
    @State() switchboardLink: string;
    @State() imageSize: 'desktopBanner' | 'mobileBanner';
    @State() imageLink: string;
    @State() color: string;
    @State() isViewed = false;

    // event listeners
    @Watch('parentWidth')
    onParentChange(newParentWidth) {
        const imageSize = getImageSize(newParentWidth);
        if (imageSize !== this.imageSize) {
            this.setNewLinks(imageSize);
        }
    }

    onView() {
        this.isViewed = true;

        if (!Build.isTesting && !Build.isServer) {
            this.logPromotionEvent('Impression');
            fireImpressions(
                this.promotion.impressionUrls,
                this.promotion.affiliateId,
                this.promotion.impressionTtl,
                this.promotion.impressionThrottle,
                this.promotion.impressionThrottleClick
            );
        }
    }

    connectedCallback() {
        if (!Build.isTesting && !Build.isServer) {
            registerElToObserverList(this.el, this.onView.bind(this));
        } else {
            this.onView();
        }

        this.setBannerProperties();
    }

    setBannerProperties() {
        const paidMarketing = getQueryParamFromUrl('paidMarketing');

        const switchboardLink =
            this.promotion.alternateLink ||
            createSwitchboardLink(this.promotion.switchboard_domain, {
                affiliateId: this.promotion.bookId,
                campaignId: this.promotion.campaignId,
                stateCode: this.location,
                context: this.context,
                propertyId: this.propertyId,
                options: paidMarketing,
            });

        const color = this.promotion.logoColor;

        // Insert markup
        const imageSize = getImageSize(this.parentWidth);
        const imageLink = this.promotion[imageSize];

        this.switchboardLink = switchboardLink;
        this.imageSize = imageSize;
        this.imageLink = imageLink;
        this.color = color;

        this.setNewLinks(imageSize);
    }

    setNewLinks(imageSize: 'desktopBanner' | 'mobileBanner') {
        const paidMarketing = getQueryParamFromUrl('paidMarketing');

        const promotion = this.promotion;
        if (!promotion) {
            return;
        }

        const switchboardLink =
            promotion.alternateLink ||
            createSwitchboardLink(this.promotion.switchboard_domain, {
                affiliateId: promotion.bookId,
                campaignId: promotion.campaignId,
                stateCode: this.location,
                context: this.context,
                propertyId: this.propertyId,
                options: paidMarketing,
            });

        const imageLink = promotion[imageSize];

        this.imageSize = imageSize;
        this.imageLink = imageLink;
        this.switchboardLink = switchboardLink;
    }

    logPromotionEvent(action: 'Impression' | 'Click') {
        if (action === 'Click') {
            this.callback();
        }
        const i13n = getPromotionEvent({
            componentType: this.bannerType,
            action,
            options: {
                ...this.eventOptions,
                stateCode: this.location,
                context: this.context,
                propertyId: this.propertyId,
            },
            promotion: this.promotion,
        });

        debouncedLogEvent(this.propertyId, i13n);
    }

    render() {
        const height = this.imageSize === 'desktopBanner' ? 280 : 150;
        const width = this.imageSize === 'desktopBanner' ? 3200 : 1125;

        return (
            <Host>
                <NoFollowLink
                    id="promo-container"
                    style={{
                        backgroundColor: this.color ? `#${this.color}` : '#aaaaaa',
                        width: `${this.parentWidth}px`,
                        height: `${this.parentWidth * (height / width)}px`,
                        display: 'block',
                    }}
                    class="banner"
                    onClick={this.logPromotionEvent.bind(this, 'Click')}
                    href={this.switchboardLink}
                >
                    {this.isViewed ? (
                        <picture>
                            <source srcSet={createSrcSet(this.imageLink, width, height)} type="image/webp" />
                            <img
                                class={`lazy-logo ${LazyLoadClassName.BANNER_IMAGE}`}
                                sizes="(max-width: 1400px) 100vw, 1400px"
                                src={this.imageLink}
                                alt="promotion banner image"
                                width={this.parentWidth}
                                height={this.parentWidth * (height / width)}
                            />
                        </picture>
                    ) : (
                        <div
                            style={{
                                width: `${this.parentWidth}px`,
                                height: `${this.parentWidth * (height / width)}px`,
                            }}
                        >
                            &nbsp;
                        </div>
                    )}
                </NoFollowLink>
            </Host>
        );
    }
}
